import { Box, CircularProgress, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import React from "react";
import { STATUS_COLOR } from "../../constants";
import { COLORS } from "../../theme/theme-options";

type Size = "sm" | "md";

const sizeMap: Record<Size, { px: number; thickness: number; fontSize: number }> = {
  sm: { px: 40, thickness: 3.5, fontSize: 12 },
  md: { px: 44, thickness: 4, fontSize: 14 },
};

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
const clamp100 = (v: number) => Math.max(0, Math.min(100, v));

const normalizeScore = (raw: number) => {
  if (raw >= 0 && raw <= 1) { 
    return { progress: clamp01(raw) * 100, label: Math.round(raw * 100) }; 
  }
  return { 
    progress: clamp100(raw), label: Math.round(clamp100(raw)) 
  };
};

const colorForScore = (score0to100: number) => {
  if (score0to100 >= 70) { 
    return STATUS_COLOR.healthy.main 
  };
  if (score0to100 >= 50) { 
    return STATUS_COLOR.warning.main; 
  }
  return STATUS_COLOR.critical.main;
};

export type HealthScoreProps = {
  score: number;
  size?: Size;
};

export const HealthScore = ({ score, size = "sm" }: HealthScoreProps) => {
  const s = sizeMap[size];
  const n = normalizeScore(score);
  const color = colorForScore(n.progress);

  return (
    <Box
      sx={{
        width: s.px,
        height: s.px,
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <CircularProgress
        variant="determinate"
        value={100}
        size={s.px}
        thickness={s.thickness}
        sx={{
          position: "absolute",
          inset: 0,
          color: alpha(COLORS.grey[500], 0.22),
          "& .MuiCircularProgress-circle": { strokeLinecap: "round" },
        }}
      />
      <CircularProgress
        variant="determinate"
        value={n.progress}
        size={s.px}
        thickness={s.thickness}
        sx={{
          position: "absolute",
          inset: 0,
          color,
          filter: `drop-shadow(0 0 6px ${alpha(color, 0.25)})`,
          "& .MuiCircularProgress-circle": {
            strokeLinecap: "round",
            transition: "stroke-dashoffset 700ms cubic-bezier(0.4, 0, 0.2, 1)",
          },
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        <Typography
          sx={{
            fontSize: s.fontSize,
            fontWeight: 800,
            color: COLORS.grey[100],
            lineHeight: 1,
            letterSpacing: 0.2,
          }}
        >
          {n.label}
        </Typography>
      </Box>
    </Box>
  );
};
